import { MainLayoutContainer } from "components/Layout";
import { useSurveyAnswers } from "./useSurveyAnswers";
import {
  Box,
  CardContent,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Divider,
  Chip,
  Avatar,
} from "@mui/material";
import { CardOutline } from "components/Card";
import { Answer } from "types/answer";
import { getAmericanDateTime } from "utils/Datetime";

export const SurveyAnswers = () => {
  const { loading, organizationId, surveyId, answers, surveyName, error } =
    useSurveyAnswers();

  // Get user info from first answer (all answers in this view are from same submission)
  const submissionUser = answers.length > 0 ? answers[0].user : undefined;
  const submissionDate = answers.length > 0 ? answers[0].created_at : undefined;

  // Group answers by question
  const groupedAnswers = answers.reduce(
    (acc, answer) => {
      const questionId = answer.question_id;
      if (!acc[questionId]) {
        acc[questionId] = {
          question: answer.question,
          answers: [],
        };
      }
      acc[questionId].answers.push(answer);
      return acc;
    },
    {} as Record<string, { question: any; answers: Answer[] }>,
  );

  const sortedQuestions = Object.entries(groupedAnswers)
    .map(([questionId, data]) => ({
      questionId,
      question: data.question,
      answers: data.answers,
    }))
    .sort((a, b) => (a.question?.order || 0) - (b.question?.order || 0));

  if (loading) {
    return <div>loading...</div>;
  }

  if (error) {
    return (
      <MainLayoutContainer
        title="Survey Answers"
        loading={false}
        error={{ message: error.message || "Error loading answers" }}
      >
        <div>Error loading answers</div>
      </MainLayoutContainer>
    );
  }

  return (
    <MainLayoutContainer
      title={surveyName || "Survey Answers"}
      loading={loading}
      error={undefined}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <CardOutline>
              <CardContent>
                <Typography
                  gutterBottom
                  component="div"
                  fontWeight={600}
                  variant="h6"
                >
                  Survey Information
                </Typography>
                <Box mt={2}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {surveyName || "Loading..."}
                  </Typography>
                </Box>
                {submissionUser && (
                  <Box mt={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {submissionUser.picture_url ? (
                        <Avatar
                          src={submissionUser.picture_url}
                          alt={submissionUser.name || "User"}
                          sx={{ width: 40, height: 40 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {(submissionUser.name || "U")[0].toUpperCase()}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {submissionUser.name || "Anonymous User"}
                        </Typography>
                        {submissionDate && (
                          <Typography variant="body2" color="text.secondary">
                            Submitted: {getAmericanDateTime(submissionDate)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </CardOutline>
          </Grid>

          <Grid size={12}>
            <CardOutline>
              <CardContent>
                <Typography
                  gutterBottom
                  component="div"
                  fontWeight={600}
                  variant="h6"
                >
                  Questions & Answers ({sortedQuestions.length})
                </Typography>
                <Divider sx={{ my: 2 }} />
                {sortedQuestions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No answers found for this submission.
                  </Typography>
                ) : (
                  <List>
                    {sortedQuestions.map((item, index) => (
                      <Box key={item.questionId || index}>
                        <ListItem
                          sx={{
                            flexDirection: "column",
                            alignItems: "flex-start",
                            py: 2,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            width="100%"
                          >
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={
                                item.question?.type === "multiple_choice"
                                  ? "Multiple Choice (select multiple)"
                                  : item.question?.type === "single_choice"
                                    ? "Multiple Choice (select one)"
                                    : item.question?.type === "likert"
                                    ? "Likert Scale"
                                    : "Fill in the blank"
                              }
                              size="small"
                              color={
                                item.question?.type === "multiple_choice"
                                  ? "secondary"
                                  : item.question?.type === "single_choice"
                                    ? "info"
                                    : "default"
                              }
                            />
                            <Chip
                              label={`${item.answers.length} Answer${item.answers.length !== 1 ? "s" : ""}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Stack>
                          <Box mt={1} width="100%">
                            <Typography variant="subtitle1" fontWeight={600}>
                              {item.question?.title || "Untitled Question"}
                            </Typography>
                          </Box>
                          {item.question?.body && (
                            <Box mt={1} width="100%">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.question.body}
                              </Typography>
                            </Box>
                          )}
                          {(item.question?.type === "multiple_choice" ||
                            item.question?.type === "single_choice") &&
                            item.question?.options &&
                            item.question.options.length > 0 && (
                              <Box mt={2} width="100%">
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Options:
                                </Typography>
                                <List dense sx={{ pl: 2 }}>
                                  {item.question.options
                                    .sort(
                                      (a: any, b: any) =>
                                        (a.order || 0) - (b.order || 0),
                                    )
                                    .map((option: any, optIndex: any) => (
                                      <ListItem
                                        key={option.option_id || optIndex}
                                        sx={{ py: 0.5 }}
                                      >
                                        <ListItemText
                                          primary={
                                            <Typography variant="body2">
                                              {String.fromCharCode(
                                                65 + optIndex,
                                              )}
                                              . {option.text}
                                            </Typography>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                </List>
                              </Box>
                            )}
                          <Box mt={2} width="100%">
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Answers ({item.answers.length}):
                            </Typography>
                            <List dense sx={{ pl: 2, mt: 1 }}>
                              {item.answers.map((answer, ansIndex) => (
                                <ListItem
                                  key={answer.answer_id || ansIndex}
                                  sx={{ py: 1 }}
                                >
                                  <ListItemText
                                    primary={
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          Answer {ansIndex + 1}:
                                        </Typography>
                                        <Typography variant="body2">
                                          {answer.option
                                            ? answer.option.text
                                            : answer.answer}
                                        </Typography>
                                        {answer.option && (
                                          <Chip
                                            label="Selected Option"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </Stack>
                                    }
                                    secondary={
                                      answer.option &&
                                      answer.answer !== answer.option.text ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mt: 0.5 }}
                                        >
                                          Full text: {answer.answer}
                                        </Typography>
                                      ) : null
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        </ListItem>
                        {index < sortedQuestions.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </CardOutline>
          </Grid>
        </Grid>
      </Box>
    </MainLayoutContainer>
  );
};
